// src/contexts/NotificationContext.types.ts

import type {
  NotificationPreferences,
} from "../types/Settings.types";

export type {
  NotificationPreferences,
} from "../types/Settings.types";

export type NotificationPriority =
  | "low"
  | "normal"
  | "high"
  | "critical";

export type NotificationType =
  // General
  | "alert"
  | "reminder"
  | "message"
  | "completed"
  | "update"
  | "system_update"

  // Hospital
  | "appointment"
  | "appointment_created"
  | "appointment_updated"
  | "appointment_cancelled"
  | "consultation"
  | "consultation_reminder"
  | "prescription"
  | "patient_assigned"
  | "patient_update"
  | "urgent_patient_alert"
  | "staff_update"
  | "facility_update"

  // Emergency
  | "emergency_alert"
  | "emergency_update"
  | "emergency_resolved"
  | "ambulance_dispatched"

  // Laboratory
  | "test_request"
  | "test_result"
  | "urgent"
  | "sample_collected"
  | "result_ready"
  | "quality_alert"
  | "equipment_alert"
  | "low_inventory"

  // Insurance
  | "claim"
  | "insurance_claim"
  | "claim_update"
  | "pre_auth"
  | "verification"
  | "enrollment_update";

export type NotificationSource =
  // Canonical source values
  | "hospital"
  | "laboratory"
  | "insurance"
  | "system"

  // Existing application source values
  | "Appointment"
  | "Doctor"
  | "Emergency"
  | "Lab"
  | "General";

export interface Notification {
  id: string;
  title: string;
  message: string;

  type: NotificationType;
  source: NotificationSource;
  priority: NotificationPriority;

  timestamp: number;
  time: string;
  read: boolean;

  color?: string;
  bg?: string;

  expiresAt?: number;
  actionUrl?: string;
  metadata?: Record<string, unknown>;
}

export type NewNotification = Omit<
  Notification,
  | "id"
  | "timestamp"
  | "time"
  | "read"
  | "priority"
> & {
  priority?: NotificationPriority;
};

export interface NotificationTemplate {
  priority: NotificationPriority;
  color: string;
  bg: string;
}

export interface NotificationSettings {
  soundEnabled: boolean;
  desktopEnabled: boolean;
  emailEnabled: boolean;
  smsEnabled: boolean;

  mutedSources: NotificationSource[];
  mutedTypes: NotificationType[];

  doNotDisturbStart?: string;
  doNotDisturbEnd?: string;
}

export interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;

  pushNotification: (
    notification: NewNotification
  ) => void;

  markAsRead: (
    id: string
  ) => void;

  markAllAsRead:
    () => void;

  deleteNotification: (
    id: string
  ) => void;

  clearAllNotifications:
    () => void;

  settings: NotificationSettings;

  soundEnabled: boolean;
  desktopEnabled: boolean;

  updateSettings: (
    updates: Partial<NotificationSettings>
  ) => void;

  toggleSound:
    () => void;

  toggleDesktop:
    () => void;

  notificationPreferences:
    NotificationPreferences;

  updateNotificationPreferences: (
    updates: Partial<NotificationPreferences>
  ) => void;

  isNotificationPanelOpen:
    boolean;

  openNotificationPanel:
    () => void;

  closeNotificationPanel:
    () => void;

  toggleNotificationPanel:
    () => void;

  getNotificationsBySource: (
    source: NotificationSource
  ) => Notification[];

  getNotificationsByType: (
    type: NotificationType
  ) => Notification[];

  getUnreadNotifications:
    () => Notification[];
}

const NEUTRAL_BACKGROUND =
  "#F7F9F9";

const DEFAULT_TEXT =
  "#43575C";

const BRAND_TEXT =
  "#117E78";

const SUCCESS_TEXT =
  "#315F52";

const DANGER_TEXT =
  "#A34747";

const DEFAULT_TEMPLATE:
  NotificationTemplate = {
    priority:
      "normal",

    color:
      BRAND_TEXT,

    bg:
      NEUTRAL_BACKGROUND,
  };

const SUCCESS_TEMPLATE:
  NotificationTemplate = {
    priority:
      "normal",

    color:
      SUCCESS_TEXT,

    bg:
      NEUTRAL_BACKGROUND,
  };

const UPDATE_TEMPLATE:
  NotificationTemplate = {
    priority:
      "low",

    color:
      DEFAULT_TEXT,

    bg:
      NEUTRAL_BACKGROUND,
  };

const ALERT_TEMPLATE:
  NotificationTemplate = {
    priority:
      "high",

    color:
      DANGER_TEXT,

    bg:
      "#FCF8F8",
  };

const CRITICAL_TEMPLATE:
  NotificationTemplate = {
    priority:
      "critical",

    color:
      DANGER_TEXT,

    bg:
      "#FCF8F8",
  };

export const NOTIFICATION_TEMPLATES:
  Partial<
    Record<
      NotificationType,
      NotificationTemplate
    >
  > & {
    default:
      NotificationTemplate;
  } = {
    default:
      DEFAULT_TEMPLATE,

    // General
    alert:
      ALERT_TEMPLATE,

    reminder:
      DEFAULT_TEMPLATE,

    message:
      DEFAULT_TEMPLATE,

    completed:
      SUCCESS_TEMPLATE,

    update:
      UPDATE_TEMPLATE,

    system_update:
      UPDATE_TEMPLATE,

    // Hospital
    appointment:
      DEFAULT_TEMPLATE,

    appointment_created:
      DEFAULT_TEMPLATE,

    appointment_updated:
      UPDATE_TEMPLATE,

    appointment_cancelled:
      ALERT_TEMPLATE,

    consultation:
      DEFAULT_TEMPLATE,

    consultation_reminder:
      DEFAULT_TEMPLATE,

    prescription:
      DEFAULT_TEMPLATE,

    patient_assigned:
      DEFAULT_TEMPLATE,

    patient_update:
      UPDATE_TEMPLATE,

    urgent_patient_alert:
      CRITICAL_TEMPLATE,

    staff_update:
      UPDATE_TEMPLATE,

    facility_update:
      UPDATE_TEMPLATE,

    // Emergency
    emergency_alert:
      CRITICAL_TEMPLATE,

    emergency_update:
      ALERT_TEMPLATE,

    emergency_resolved:
      SUCCESS_TEMPLATE,

    ambulance_dispatched:
      ALERT_TEMPLATE,

    // Laboratory
    test_request:
      DEFAULT_TEMPLATE,

    test_result:
      SUCCESS_TEMPLATE,

    urgent:
      CRITICAL_TEMPLATE,

    sample_collected:
      DEFAULT_TEMPLATE,

    result_ready:
      SUCCESS_TEMPLATE,

    quality_alert:
      ALERT_TEMPLATE,

    equipment_alert:
      ALERT_TEMPLATE,

    low_inventory:
      ALERT_TEMPLATE,

    // Insurance
    claim:
      DEFAULT_TEMPLATE,

    insurance_claim:
      DEFAULT_TEMPLATE,

    claim_update:
      UPDATE_TEMPLATE,

    pre_auth:
      ALERT_TEMPLATE,

    verification:
      DEFAULT_TEMPLATE,

    enrollment_update:
      UPDATE_TEMPLATE,
  };